import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  Observable,
  ServerError,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GRAPHQL_URL, IS_SCREENSHOT_MODE } from '../../config/constants';
import { GraphQLError } from 'graphql';
import * as Sentry from '@sentry/browser';
import { Severity } from '@sentry/browser';
import { toString as loDashToString } from 'lodash';
import { ErrorType } from './graphqlSchema';
import Cognito from '../../pages/Auth/utils/Cognito';
import { appRoutes } from '../../path';
import { AxiosError } from 'axios';
import { ICognitoResponse } from '../../pages/Auth/LoginPages/types';
import toastMessage from '../toastMessage';
import { validateRedirectUrl } from '../embedUrlValidation';
import { POPUP_WINDOW_NAME } from '../../pages/Auth/utils/utils';

const httpLink = new HttpLink({
  uri: GRAPHQL_URL,
  credentials: 'include',
});
const errorLink = onError(
  ({ response, graphQLErrors, networkError, operation, forward }) => {
    if (operation.operationName === 'IntrospectionQuery' && response) {
      response.errors = undefined;
      return;
    }
    if (networkError) {
      console.error('Network error: ', networkError);
      if (
        networkError?.name === 'ServerError' &&
        (networkError as ServerError)?.statusCode === 401 &&
        !IS_SCREENSHOT_MODE
      ) {
        // when 401 happened, try to refresh token and retry the request
        // if not able to refresh token, logout and redirect to login page
        // simply forward the operator doesn't work.
        // therefore I found this observe pattern from apollo related discussion
        return new Observable((observer) => {
          Cognito.refreshToken()
            .then(() => {
              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              };

              // Retry the failed request
              forward(operation).subscribe(subscriber);
            })
            .catch((err: AxiosError<ICognitoResponse>) => {
              Cognito.setRedirectUrl(window.location.pathname);
              console.error('Error on refreshing token', err);

              // Close the popup window if token refresh fails
              if (window.name === POPUP_WINDOW_NAME) {
                window.close();
              }

              const isEmbedPath = window.location.pathname.startsWith('/embed');

              if (isEmbedPath) {
                const validatedPath = validateRedirectUrl(
                  window.location.pathname,
                );
                if (validatedPath) {
                  window.location.href = `${appRoutes.embedLogin}?redirect=${validatedPath}`;
                  return;
                }
              }

              window.location.href = Cognito.getLoginPageUrl();
            });
        });
      }
      // ignore the network error
      if (operation?.query?.kind !== 'Document') {
        Sentry.captureException(networkError, {
          extra: {
            ...operation,
          },
        });
        if (response) {
          response.errors = undefined;
        }
      }
    }

    if (graphQLErrors) {
      graphQLErrors.forEach((error: GraphQLError) => {
        const errorType = error.extensions?.classification;
        const debugMessage = error.extensions?.debugMessage;
        console.error('Error Operation', operation);
        console.error(
          `Error Type: [${errorType}], debugMessage: [${debugMessage}]`,
        );
        console.error(
          'Error Message',
          JSON.stringify(error.message),
          JSON.stringify(response),
        );
        Sentry.captureMessage(`${error.message} (${debugMessage})`, {
          level: Severity.Fatal,
          extra: {
            ...error,
            ...operation,
          },
        });
        if (errorType === ErrorType.MissingPermission) {
          toastMessage({
            message: `${error.message}`,
            type: 'error',
          });
        } else if (window.location.pathname !== appRoutes.errorPage) {
          window.location.href =
            `/error?type=${encodeURIComponent(loDashToString(errorType))}` +
            `&message=${encodeURIComponent(loDashToString(error.message))}` +
            `&operation=${encodeURIComponent(
              loDashToString(operation?.operationName),
            )}`;
        }
      });
    }
  },
);

const link = ApolloLink.from([errorLink, httpLink]);
const client = new ApolloClient({
  link,
  cache: new InMemoryCache({
    // The cache automatically requests the __typename field for every object in your outgoing operations,
    // which means you can omit __typename from your operation definitions.
    // must be true because nonOptionalTypename: true is set in codegen.yml
    addTypename: true,
  }),
  connectToDevTools: true,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'no-cache',
    },
    query: {
      fetchPolicy: 'no-cache',
    },
    mutate: {
      fetchPolicy: 'no-cache',
    },
  },
});

export default client;
